import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";
import {
  ShieldCheck,
  Heart,
  HandPointing,
  Sparkle,
  Scroll as ScrollIcon,
  CheckCircle,
} from "@phosphor-icons/react";

const POLICY_VERSION = "v1";
const STORAGE_KEY = "launchpad_terms_accepted";

export function hasAcceptedTerms() {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === POLICY_VERSION;
}

export function clearTermsAcceptance() {
  if (typeof window !== "undefined") localStorage.removeItem(STORAGE_KEY);
}

export default function PolicyDialog({ open, onAccept }) {
  const [agreed, setAgreed] = useState(false);

  const handleAccept = () => {
    if (!agreed) return;
    localStorage.setItem(STORAGE_KEY, POLICY_VERSION);
    onAccept();
  };

  return (
    <Dialog open={open}>
      <DialogContent
        data-testid="policy-dialog"
        className="bg-deck-sidebar border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto"
        // Disable close on outside click - user must accept
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
      >
        <DialogHeader>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-[#2563EB] flex items-center justify-center deck-tile">
              <ScrollIcon size={24} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#FACC15]">
                ANTES DE EMPEZAR
              </div>
              <DialogTitle className="text-white font-heading text-2xl">
                Términos y políticas
              </DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-white/60 font-mono text-xs leading-relaxed">
            Por favor lee y acepta los siguientes puntos antes de iniciar sesión en LaunchPad.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          <PolicySection
            icon={<Heart size={18} weight="fill" className="text-[#EF4444]" />}
            title="LaunchPad es gratis"
            body="Esta aplicación es 100% gratuita para uso personal. No hay planes de pago, ni anuncios, ni cobros ocultos. Si en el futuro se añaden funciones premium, las actuales seguirán siendo gratuitas."
          />

          <PolicySection
            icon={<ShieldCheck size={18} weight="fill" className="text-[#2563EB]" />}
            title="Privacidad"
            body="Solo guardamos lo que tú creas (tus decks, botones y sesión de Google). No vendemos ni compartimos tus datos con terceros. Puedes eliminar tu cuenta y todos tus datos cuando quieras escribiendo a soporte."
          />

          <PolicySection
            icon={<HandPointing size={18} weight="fill" className="text-[#FACC15]" />}
            title="Tu responsabilidad"
            body="Eres responsable de los enlaces y contenido que guardes. No incluyas links a contenido ilegal, malware o que viole derechos de terceros. La app extrae vistas previas públicas de los sitios pero no controla su contenido."
          />

          <PolicySection
            icon={<Sparkle size={18} weight="fill" className="text-white" />}
            title="Sin garantía"
            body="LaunchPad se ofrece tal cual, sin garantías. Hacemos copias de respaldo razonables pero te recomendamos no depender exclusivamente de la app para información crítica. No nos hacemos responsables de pérdidas de datos por causas de fuerza mayor."
          />

          <PolicySection
            icon={<CheckCircle size={18} weight="fill" className="text-[#FACC15]" />}
            title="Acuerdo"
            body="Al continuar aceptas estos términos. Puedes revocar tu consentimiento en cualquier momento cerrando sesión y borrando tu cuenta. Esta versión de los términos es v1 con fecha de febrero 2026."
          />
        </div>

        <label
          data-testid="terms-checkbox-label"
          className="mt-6 flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 cursor-pointer hover:bg-white/8 transition"
        >
          <Checkbox
            data-testid="terms-checkbox"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(v === true)}
            className="mt-0.5 border-white/30 data-[state=checked]:bg-[#2563EB] data-[state=checked]:border-[#2563EB]"
          />
          <span className="text-sm text-white/85 leading-relaxed">
            He leído y acepto los términos, la política de privacidad y mis
            responsabilidades como usuario.
          </span>
        </label>

        <DialogFooter className="mt-4">
          <Button
            data-testid="terms-accept-btn"
            onClick={handleAccept}
            disabled={!agreed}
            className="w-full bg-[#2563EB] text-white hover:bg-[#1D4ED8] disabled:opacity-40 disabled:cursor-not-allowed font-heading font-bold py-6"
          >
            Continuar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PolicySection({ icon, title, body }) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-heading font-bold text-sm text-white">{title}</div>
        <div className="text-xs font-mono text-white/60 mt-0.5 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}
